import classNames from 'classnames';
import * as React from 'react';
import {
  injectStripe,
  CardNumberElement,
  CardExpiryElement,
  CardCVCElement,
  ReactStripeElements
} from '../../../../utils/Stripe';
import { SetupCardFunction } from '../../../../stores';
import { AsyncLoader } from '../../../Common/AsyncLoader';
import { Button } from '../../../Forms/Button';
import { PaymentErrors } from './PaymentErrors';
import { PostcodeInput } from './PostcodeInput';
import './style.css';

export interface PaymentFormProps {
  className?: string;
  onSubmit: (setupCard: SetupCardFunction) => void;
  paymentError?: Error;
  paymentRequestRunning?: boolean;
  ref?: React.RefObject<{ getWrappedInstance(): PaymentForm | undefined }>;
  submitButtonText?: string;
}

type InputStateKeys<T> = {
  [K in keyof T]: T[K] extends InputState ? InputState : never;
};

export interface InputState {
  complete?: boolean;
  invalid?: boolean;
  errorMessage?: string;
  value?: string;
}

export interface PaymentFormState {
  cardNumber: InputState;
  cardExpiry: InputState;
  cardCVC: InputState;
  elements: stripe.elements.Element[];
  postCode: InputState;
}

export class CardSetupError extends Error {}

interface StripeElementStyle {
  base?: stripe.elements.Style;
  complete?: stripe.elements.Style;
  empty?: stripe.elements.Style;
  invalid?: stripe.elements.Style;
}

const stripeElementsStyling: StripeElementStyle = {
  base: {
    color: 'rgb(37, 37, 37)',
    fontFamily: `'Roboto', sans-serif`,
    '::placeholder': {
      color: 'rgb(120, 120, 120)'
    },
    // Since the input is rendered in an iframe, the base font size isn't 10,
    // so we can't use rems
    fontSize: '16px'
  },
  invalid: {
    color: 'rgb(250, 0, 0)',
    fontWeight: 700
  }
};

export class PaymentForm extends React.Component<
  PaymentFormProps,
  PaymentFormState
> {
  public state: PaymentFormState = {
    cardNumber: {},
    cardExpiry: {},
    cardCVC: {},
    elements: [],
    postCode: {}
  };

  private postcodeRef = React.createRef<PostcodeInput>();

  render() {
    const { className, submitButtonText } = this.props;
    const { cardCVC, cardExpiry, cardNumber, elements, postCode } = this.state;

    return (
      <form
        className={classNames(`payment-form`, className)}
        onSubmit={this.handleSubmit}
      >
        <div className="payment-form__content-wrapper">
          <div className="payment-form__inputs-wrapper">
            <CardNumberElement
              className={classNames({
                'payment-form__card-input': true,
                'payment-form__stripe-input': true,
                'payment-form__input--invalid': cardNumber.invalid
              })}
              onChange={this.getChangeHandler('cardNumber')}
              onReady={el => elements.push(el)}
              placeholder="Card number"
              style={stripeElementsStyling}
            />

            <CardExpiryElement
              className={classNames({
                'payment-form__card-expiry-input': true,
                'payment-form__stripe-input': true,
                'payment-form__input--invalid': cardExpiry.invalid
              })}
              onChange={this.getChangeHandler('cardExpiry')}
              onReady={el => elements.push(el)}
              placeholder="Expiry"
              style={stripeElementsStyling}
            />

            <CardCVCElement
              className={classNames({
                'payment-form__cvc-input': true,
                'payment-form__stripe-input': true,
                'payment-form__input--invalid': cardCVC.invalid
              })}
              onChange={this.getChangeHandler('cardCVC')}
              onReady={el => elements.push(el)}
              placeholder="CVC"
              style={stripeElementsStyling}
            />

            <PostcodeInput
              className="payment-form__postcode-input"
              invalid={postCode.invalid}
              onChange={this.getChangeHandler('postCode')}
              ref={this.postcodeRef}
              placeholder="Postcode"
              value={postCode.value || ''}
            />
          </div>

          <Button
            className="payment-form__submit-button"
            type="submit"
            disabled={this.submitDisabled()}
          >
            {submitButtonText ? submitButtonText : `Subscribe`}
          </Button>

          <PaymentErrors
            className="payment-form__errors"
            errors={this.getErrors()}
          />

          {this.getPaymentErrorInfo()}

          {this.getLoader()}
        </div>
      </form>
    );
  }

  public clearInputs = () => {
    const {
      postcodeRef,
      state: { elements }
    } = this;

    if (postcodeRef.current) {
      postcodeRef.current.clear();
    }

    for (let element of elements) {
      element.clear();
    }

    this.setState({
      cardNumber: {},
      cardExpiry: {},
      cardCVC: {},
      postCode: {}
    });
  };

  private getChangeHandler = (
    inputStateKey: keyof InputStateKeys<PaymentFormState>
  ) => {
    return (event: stripe.elements.ElementChangeResponse) =>
      this.updateInputState(inputStateKey, event);
  };

  private updateInputState = (
    inputStateKey: keyof InputStateKeys<PaymentFormState>,
    event: stripe.elements.ElementChangeResponse
  ) => {
    const { [inputStateKey]: inputState } = this.state;

    const newState = {
      [inputStateKey]: {
        ...inputState,
        complete: event.complete,
        errorMessage: event.error ? event.error.message : undefined,
        invalid: !!event.error,
        value: event.value
      }
    } as {
      [P in keyof InputStateKeys<PaymentFormState>]: InputState;
    };

    this.setState(newState as PaymentFormState);
  };

  private submitDisabled = (): boolean => {
    const { paymentRequestRunning } = this.props;

    if (paymentRequestRunning) {
      return true;
    }

    const { cardCVC, cardExpiry, cardNumber, postCode } = this.state;

    return [cardCVC, cardExpiry, cardNumber, postCode].some(
      (inputState: InputState) => inputState.invalid || !inputState.complete
    );
  };

  private handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const {
      props,
      state: { postCode }
    } = this;
    const { onSubmit } = props;

    event.preventDefault();

    if (this.submitDisabled()) {
      return;
    }

    const stripe = (props as ReactStripeElements.InjectedStripeProps).stripe;

    if (!stripe) {
      // TODO: Warn?
      return;
    }

    return onSubmit(async (clientSecret: string) => {
      const result = await stripe.handleCardSetup(clientSecret, {
        payment_method_data: {
          billing_details: {
            address: {
              postal_code: postCode.value
            }
          }
        }
      });

      if (
        result.error ||
        !result.setupIntent ||
        !result.setupIntent.payment_method
      ) {
        throw new CardSetupError(
          `There was an error while trying to setup the card`
        );
      }

      return result.setupIntent.payment_method;
    });
  };

  private getErrors = (): string[] => {
    const {
      props: { paymentError }
    } = this;

    const errors = paymentError
      ? [
          `There was an error while trying to complete payment. Please check your details and try again.`
        ]
      : [];

    return [...errors, ...this.getValidationErrors()];
  };

  private getValidationErrors = (): string[] => {
    const { state } = this;

    return ['cardNumber', 'cardExpiry', 'cardCVC', 'postCode']
      .map(inputStateKey => {
        const inputState = state[inputStateKey] as InputState;

        return inputState.errorMessage;
      })
      .filter<string>((error: string | undefined): error is string => !!error);
  };

  private getPaymentErrorInfo = () => {
    const { paymentError } = this.props;

    if (!paymentError) {
      return null;
    }

    return (
      <p className="payment-form__payment-error-info">
        If the issue persists, please contact us at{' '}
        <a className="edge-of-play-link" href="mailto:admin@edgeofplay.com">
          admin@edgeofplay.com
        </a>{' '}
        for support.
      </p>
    );
  };

  private getLoader = () => {
    const { paymentRequestRunning } = this.props;

    if (!paymentRequestRunning) {
      return null;
    }

    // NOTE: The pageUrl is never used (error={false}), so '/' is fine
    return (
      <div className="payment-form__loader-mask">
        <AsyncLoader error={false} loading={true} pageUrl="/" />
      </div>
    );
  };
}

export default injectStripe(PaymentForm, { withRef: true });



// WEBPACK FOOTER //
// ./src/app/components/Premium/Checkout/PaymentForm/index.tsx